import { useState } from "react";

import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import FilterSingleSelectDropdown from "../../components/filterDropdown/FilterSingleSelectDropdown";
import DateRangeFilter from "../../components/filterDropdown/DateRangeFilter";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { orderStatusOptions } from "../../shared/utility";
import { createSnackbarHelpers } from "../../shared/snackbar";

import { axiosApiInstance } from "../../axios";

export function OrdersViewModel(logout, addSnackbar) {
  const defaultFilters = {
    customer: { value: -1, label: "All customers" },
    startDate: null,
    endDate: null,
    carrier: { value: -1, label: "All carriers" },
    status: { value: -1, label: "Order status" },
  };
  const [filters, setFilters] = useState(defaultFilters);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [totalOrders, setTotalOrderss] = useState(0);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [carriers, setCarriers] = useState([]);
  const [customers, setCustomers] = useState([]);

  const { showSuccess } = createSnackbarHelpers(addSnackbar);
  const showErrorModal = (subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title="Error"
        />
      ),
      open: true,
    });
  };

  const getOrders = async function (search, page) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
      filter: filters,
    };
    let callResult = await makePostApiCall(`orders`, payload);
    if (callResult.success) {
      const response = callResult.data;
      if (page > 1) {
        setOrders([...orders, ...response.data]);
      } else {
        setLoading(false);
        setOrders(response.data);
        setTotalOrderss(response.total);
      }
      setLoading(false);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      setLoading(false);
      showErrorModal(callResult.data.message);
    }
  };

  const deleteOrder = async function (orderId, index) {
    let callResult = await makePostApiCall(`deleteOrder/${orderId}`, {});
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getOrders(null, 0);
      showSuccess(callResult.data.message);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const askDeleteOrder = function (order, index) {
    if (order.status === 5 || order.status === 11) {
      showErrorModal("Shipped or voided orders cannot be deleted");
    } else {
      setEmptyModelOpen({
        childComponent: (
          <DeleteConfirmationForm
            buttonMessage="Delete"
            confirmAction={() => deleteOrder(order.id, index)}
            message={`Are you sure you want to delete ${order.po_number} for ${order.customer_name}?`}
            setOpen={setEmptyModelOpen}
          />
        ),
        open: true,
      });
    }
  };

  const filterComponents = [
    <DateRangeFilter
      startDate={filters.startDate}
      endDate={filters.endDate}
      onChange={(name, value) =>
        setFilters({
          ...filters,
          [name]: value,
        })
      }
      resetFilter={() => setFilters(defaultFilters)}
    />,
    <FilterSingleSelectDropdown
      header="Customer"
      onChange={(value) =>
        setFilters({
          ...filters,
          customer: value,
        })
      }
      options={customers}
      resetFilter={() => setFilters(defaultFilters)}
      valueName="customer"
      value={filters.customer}
    />,
    <FilterSingleSelectDropdown
      header="Carriers"
      onChange={(value) =>
        setFilters({
          ...filters,
          carrier: value,
        })
      }
      options={carriers}
      resetFilter={() => setFilters(defaultFilters)}
      valueName="carrier"
      value={filters.carrier}
    />,
    <FilterSingleSelectDropdown
      header="Status"
      onChange={(value) =>
        setFilters({
          ...filters,
          status: value,
        })
      }
      options={orderStatusOptions}
      resetFilter={() => setFilters(defaultFilters)}
      valueName="status"
      value={filters.status}
    />,
  ];

  const resetFilters = () => {
    const filterReset = {
      customer: { value: -1, label: "All customers" },
      startDate: null,
      endDate: null,
      carrier: { value: -1, label: "All carriers" },
      status: { value: -1, label: "Order status" },
    };
    setFilters(filterReset);

    setShowFilters(false);
    getOrders(null, 1, filterReset);
  };

  const getCustomers = async function () {
    let callResult = await makeGetApiCall(`customersForDropdown`);
    if (callResult.success) {
      setCustomers(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const getCarriersForDropdown = async function () {
    let callResult = await makeGetApiCall(`carriersForDropdown`);
    if (callResult.success) {
      setCarriers(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const printShipments = async function () {
    try {
      const user_id = localStorage.getItem("id") * 1;
      const user_name = localStorage.getItem("name");

      const payload = {
        filter: filters,
        user_id,
        user_name,
      };

      const response = await axiosApiInstance.post(
        `/shipments/print`,
        payload,
        {
          responseType: "blob", // important!
          headers: {
            Authorization: "Bearer " + localStorage.getItem("authToken"),
            "Content-Type": "application/json",
          },
        },
      );

      // create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "shipments.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      } else {
        console.error("Download failed:", err);
      }
    }
  };

  return {
    askDeleteOrder,
    emptyModal,
    getOrders,
    loading,
    orders,
    setSlideOver,
    slideOver,
    setEmptyModelOpen,
    filters,
    showFilters,
    setShowFilters,
    filterComponents,
    resetFilters,
    getCustomers,
    getCarriersForDropdown,
    carriers,
    printShipments,
    totalOrders,
  };
}
